/**
 * Manisante store locator — OpenFreeMap vector basemap with OSM raster fallback.
 * Loaded only by sections/manisante-store-locator.liquid. MapLibre itself is
 * fetched lazily from theme assets when a vector map is about to be shown.
 */
window.ManisanteStoreMap = (function () {
  'use strict';

  var OSM_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  var OFM_ATTR = '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> <a href="https://www.openmaptiles.org/" target="_blank">&copy; OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>';
  var DEFAULT_STYLE = 'https://tiles.openfreemap.org/styles/positron';
  var TIMEOUT_MS = 8000;
  var MAX_LABEL_LANGS = 3;

  function getQueryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function languagesFromCode(code) {
    if (!code) return [];
    var raw = String(code).toLowerCase().replace(/_/g, '-').trim();
    if (!raw) return [];
    var parts = raw.split('-');
    var out = [];
    if (parts.length > 1) out.push(raw);
    if (parts[0]) out.push(parts[0]);
    return out;
  }

  function unique(list) {
    var seen = {};
    var out = [];
    list.forEach(function (item) {
      if (!item || seen[item]) return;
      seen[item] = true;
      out.push(item);
    });
    return out;
  }

  function visitorLanguages() {
    var override = getQueryParam('mns_map_lang');
    if (override) return languagesFromCode(override);

    var nav = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || navigator.userLanguage || ''];
    var langs = [];
    nav.forEach(function (code) {
      languagesFromCode(code).forEach(function (lang) { langs.push(lang); });
    });
    return langs;
  }

  function resolveLabelLanguages(cfg) {
    var langs = unique(visitorLanguages()).slice(0, MAX_LABEL_LANGS);
    if (cfg && cfg.locale) {
      languagesFromCode(cfg.locale).forEach(function (lang) { langs.push(lang); });
    }
    return unique(langs);
  }

  function buildNameTextField(langs) {
    var expr = ['coalesce'];
    langs.forEach(function (lang) {
      expr.push(['get', 'name:' + lang]);
    });
    expr.push(['get', 'name']);
    return expr;
  }

  function localiseStyle(style, langs) {
    if (!style || !style.layers) return style;
    var field = buildNameTextField(langs);
    style.layers.forEach(function (layer) {
      if (layer.type !== 'symbol' || !layer.layout || !layer.layout['text-field']) return;
      var tf = layer.layout['text-field'];
      var raw = typeof tf === 'string' ? tf : JSON.stringify(tf);
      if (raw.indexOf('name') === -1) return;
      layer.layout['text-field'] = field;
    });
    return style;
  }

  function hasWebGL() {
    try {
      var canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')));
    } catch (e) {
      return false;
    }
  }

  function slowConnection() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;
    return /(^|-)(2g|3g)$/.test(connection.effectiveType || '');
  }

  function assetKind(src) {
    if (!src) return 'asset';
    if (src.indexOf('leaflet-maplibre-gl') !== -1) return 'bridge';
    if (src.indexOf('maplibre-gl.css') !== -1) return 'css';
    if (src.indexOf('maplibre-gl') !== -1) return 'js';
    return 'asset';
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-mns-src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error(assetKind(src))); });
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-mns-src', src);
      script.onload = function () {
        script.setAttribute('data-loaded', 'true');
        resolve();
      };
      script.onerror = function () { reject(new Error(assetKind(src))); };
      document.head.appendChild(script);
    });
  }

  function loadCss(href) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('link[data-mns-href="' + href + '"]')) {
        resolve();
        return;
      }
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-mns-href', href);
      var settled = false;
      function finish(ok) {
        if (settled) return;
        settled = true;
        if (ok) resolve();
        else reject(new Error('css'));
      }
      link.onload = function () { finish(true); };
      link.onerror = function () { finish(false); };
      document.head.appendChild(link);
      setTimeout(function () { finish(true); }, 1500);
    });
  }

  function logFallback(reason) {
    if (window.Shopify && Shopify.designMode) {
      console.info('[Manisante store locator] using OpenStreetMap fallback:', reason);
    }
  }

  function removeGlLayer(map, state) {
    if (!state.glLayer) return;
    try {
      map.removeLayer(state.glLayer);
    } catch (e) {
      var container = state.glLayer._container;
      if (!container && state.glLayer.getContainer) container = state.glLayer.getContainer();
      if (container && container.parentNode) container.parentNode.removeChild(container);
    }
    state.glLayer = null;
  }

  function useOsm(map, mapEl, state, reason) {
    if (state.done) return;
    state.done = true;
    clearTimeout(state.timer);
    if (map.attributionControl) map.attributionControl.removeAttribution(OFM_ATTR);
    removeGlLayer(map, state);
    L.tileLayer(OSM_TILES, { maxZoom: 19, attribution: OSM_ATTR }).addTo(map);
    mapEl.setAttribute('data-map-provider', 'osm-fallback:' + reason);
    logFallback(reason);
  }

  function decideImmediateReason(cfg) {
    if ((cfg && cfg.provider) === 'osm' || getQueryParam('mns_map') === 'osm') return 'setting';
    if (!hasWebGL()) return 'no-webgl';
    if (!(cfg && cfg.vectorOnSlow) && slowConnection()) return 'slow-connection';
    return null;
  }

  function isTileSourceError(event) {
    if (!event) return false;
    if (event.sourceId === 'openmaptiles') return true;
    if (event.source && event.source.id === 'openmaptiles') return true;
    var message = (event.error && event.error.message) || event.message || '';
    return /openmaptiles|\.pbf/i.test(String(message));
  }

  function startVector(map, mapEl, state, cfg) {
    var styleUrl = (cfg.styleUrl && String(cfg.styleUrl).trim()) || DEFAULT_STYLE;
    var langs = resolveLabelLanguages(cfg);
    var assets = cfg.assets || {};
    mapEl.setAttribute('data-map-labels', langs.join(','));
    mapEl.setAttribute('data-map-locale-mode', 'visitor');
    mapEl.setAttribute('data-map-page-locale', (cfg && cfg.locale) || '');

    state.timer = setTimeout(function () {
      useOsm(map, mapEl, state, 'timeout');
    }, cfg.timeoutMs || TIMEOUT_MS);

    Promise.all([loadCss(assets.css), loadScript(assets.js)])
      .then(function () { return loadScript(assets.bridge); })
      .then(function () {
        if (state.done) return;
        if (!window.maplibregl || !L.maplibreGL) throw new Error('lib-missing');
        return fetch(styleUrl).then(function (response) {
          if (!response.ok) throw new Error('style-http-' + response.status);
          return response.json();
        });
      })
      .then(function (style) {
        if (state.done || !style) return;
        localiseStyle(style, langs);
        state.glLayer = L.maplibreGL({ style: style, attributionControl: false });
        state.glLayer.addTo(map);
        if (map.attributionControl) map.attributionControl.addAttribution(OFM_ATTR);
        var gl = state.glLayer.getMaplibreMap();
        gl.on('load', function () {
          if (state.done) return;
          clearTimeout(state.timer);
          mapEl.setAttribute('data-map-provider', 'openfreemap');
        });
        gl.on('error', function (event) {
          if (state.done) return;
          if (!gl.loaded() && !gl.isStyleLoaded()) {
            useOsm(map, mapEl, state, 'style-error');
            return;
          }
          if (isTileSourceError(event) && ++state.tileErrors >= 3) {
            useOsm(map, mapEl, state, 'tile-errors');
          }
        });
        gl.getCanvas().addEventListener('webglcontextlost', function () {
          useOsm(map, mapEl, state, 'context-lost');
        });
      })
      .catch(function (error) {
        if (map.attributionControl) map.attributionControl.removeAttribution(OFM_ATTR);
        useOsm(map, mapEl, state, 'load-failed:' + (error && error.message ? error.message : 'error'));
      });
  }

  function attach(map, mapEl, cfg) {
    cfg = cfg || {};
    var state = {
      done: false,
      glLayer: null,
      timer: null,
      tileErrors: 0,
      observer: null
    };
    mapEl._mnsMapState = state;

    function start() {
      var reason = decideImmediateReason(cfg);
      if (reason) {
        useOsm(map, mapEl, state, reason);
        return;
      }
      startVector(map, mapEl, state, cfg);
    }

    if (cfg.lazy !== false && 'IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(function (entries) {
        if (!entries[0] || !entries[0].isIntersecting) return;
        state.observer.disconnect();
        state.observer = null;
        start();
      }, { rootMargin: '200px' });
      state.observer.observe(mapEl);
    } else {
      start();
    }

    return state;
  }

  function detach(map, mapEl) {
    var state = mapEl && mapEl._mnsMapState;
    if (!state) return;
    state.done = true;
    clearTimeout(state.timer);
    if (state.observer) {
      try { state.observer.disconnect(); } catch (e) {}
    }
    if (map) removeGlLayer(map, state);
    mapEl._mnsMapState = null;
  }

  return {
    attach: attach,
    detach: detach,
    resolveLabelLanguages: resolveLabelLanguages,
    buildNameTextField: buildNameTextField,
    localiseStyle: localiseStyle,
    OSM_ATTR: OSM_ATTR,
    OFM_ATTR: OFM_ATTR
  };
})();
